// GA4, LinkedIn, and HubSpot Analytics Module
// Only loads tracking scripts after user consent

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    _linkedin_partner_id: string;
    _linkedin_data_partner_ids: string[];
    lintrk: (action: string, data?: Record<string, unknown>) => void;
  }
}

const GA4_MEASUREMENT_ID = 'G-J267X66FKS';
const LINKEDIN_PARTNER_ID = '8582714';
const HUBSPOT_ACCOUNT_ID = '148540358';

let isGA4Initialized = false;
let isLinkedInInitialized = false;
let isHubSpotInitialized = false;

export const hasConsent = (): boolean => {
  return localStorage.getItem('cookie-consent') === 'accepted';
};

export const initGA4 = (): void => {
  if (isGA4Initialized || typeof window === 'undefined') return;
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID);
  
  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  isGA4Initialized = true;
  console.log('Analytics: GA4 initialized');
};

export const initLinkedIn = (): void => {
  if (isLinkedInInitialized || typeof window === 'undefined') return;
  
  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  
  // Load LinkedIn Insight Tag script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  document.head.appendChild(script);
  
  // Initialize lintrk function
  if (!window.lintrk) {
    window.lintrk = function (a: string, b?: Record<string, unknown>) {
      (window.lintrk as unknown as { q: unknown[] }).q = (window.lintrk as unknown as { q: unknown[] }).q || [];
      (window.lintrk as unknown as { q: unknown[] }).q.push([a, b]);
    };
  }
  
  isLinkedInInitialized = true;
  console.log('Analytics: LinkedIn Insight Tag initialized');
};

export const initHubSpot = (): void => {
  if (isHubSpotInitialized || typeof window === 'undefined') return;

  // Load HubSpot tracking script (EU1 region)
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = 'hs-script-loader';
  script.async = true;
  script.defer = true;
  script.src = `//js-eu1.hs-scripts.com/${HUBSPOT_ACCOUNT_ID}.js`;
  document.head.appendChild(script);

  isHubSpotInitialized = true;
  console.log('Analytics: HubSpot initialized');
};

export const initAnalytics = (): void => {
  if (!hasConsent()) {
    console.log('Analytics: No consent, skipping initialization');
    return;
  }

  initGA4();
  initLinkedIn();
  initHubSpot();
};

export const trackPageView = (path: string): void => {
  if (!hasConsent()) return;
  
  // GA4 page view
  if (isGA4Initialized && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: document.title,
    });
  }
  
  // LinkedIn tracks page views automatically
  console.log('Analytics: Page view tracked', path);
};

export const trackEvent = (action: string, category: string, label?: string): void => {
  if (!hasConsent()) return;
  
  if (isGA4Initialized && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
    });
  }
  
  console.log('Analytics: Event tracked', { action, category, label });
};

export const trackCTAClick = (buttonName: string, page: string): void => {
  if (!hasConsent()) return;
  
  if (isGA4Initialized && window.gtag) {
    window.gtag('event', 'cta_click', {
      button_name: buttonName,
      page_path: page,
    });
  }
  
  // LinkedIn conversion tracking
  if (isLinkedInInitialized && window.lintrk) {
    window.lintrk('track', { conversion_id: buttonName });
  }
  
  console.log('Analytics: CTA click tracked', { buttonName, page });
};

export const trackCalendlyOpen = (): void => {
  if (!hasConsent()) return;
  
  if (isGA4Initialized && window.gtag) {
    window.gtag('event', 'calendly_open', {
      event_category: 'engagement',
      event_label: 'scheduling_widget',
    });
  }
  
  console.log('Analytics: Calendly open tracked');
};

export const trackCalendlyScheduled = (): void => {
  if (!hasConsent()) return;
  
  if (isGA4Initialized && window.gtag) {
    window.gtag('event', 'calendly_scheduled', {
      event_category: 'conversion',
      event_label: 'meeting_booked',
    });
  }
  
  // LinkedIn conversion for scheduled meeting
  if (isLinkedInInitialized && window.lintrk) {
    window.lintrk('track', { conversion_id: 'calendly_scheduled' });
  }
  
  console.log('Analytics: Calendly meeting scheduled tracked');
};

export type DownloadStage = 'click' | 'view' | 'success';

export interface DownloadContext {
  source: string;
  channel?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  referrer?: string;
  path?: string;
}

export const trackDownload = (
  fileName: string,
  stage: DownloadStage,
  contextOrSource?: DownloadContext | string,
): void => {
  if (!hasConsent()) return;

  const ctx: DownloadContext =
    typeof contextOrSource === 'string'
      ? { source: contextOrSource }
      : contextOrSource ?? { source: 'direct' };

  const eventNameByStage: Record<DownloadStage, string> = {
    click: 'file_download_click',
    view: 'file_view',
    success: 'file_download',
  };
  const eventName = eventNameByStage[stage];

  if (isGA4Initialized && window.gtag) {
    window.gtag('event', eventName, {
      event_category: stage === 'view' ? 'engagement' : 'download',
      event_label: fileName,
      file_name: fileName,
      source: ctx.source ?? 'direct',
      channel: ctx.channel,
      medium: ctx.medium,
      campaign: ctx.campaign,
      content: ctx.content,
      term: ctx.term,
      referrer: ctx.referrer,
      entry_path: ctx.path,
      engagement_type: stage,
    });
  }

  // LinkedIn: count successful downloads only (views are lower-intent)
  if (stage === 'success' && isLinkedInInitialized && window.lintrk) {
    window.lintrk('track', { conversion_id: `download_${fileName}` });
  }

  console.log('Analytics: Download tracked', { fileName, stage, ...ctx });
};
